import React, { useRef, useEffect, useState } from 'react';
import './LogoLoop.css';

const LogoLoop = ({
  logos = [],
  speed = 100,
  direction = 'left',
  width = '100%',
  logoHeight = 60,
  gap = 60,
  hoverSpeed = 0,
  fadeOut = true,
  fadeOutColor = 'transparent',
  scaleOnHover = true,
  ariaLabel = 'Partner logos',
  className = '',
  style = {}
}) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef(null);
  const positionRef = useRef(0);

  const isHorizontal = direction === 'left' || direction === 'right';
  const reverseDirection = direction === 'right' || direction === 'down';

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    
    if (!container || !content) return;

    const animate = () => {
      const currentSpeed = isHovered ? hoverSpeed : speed;
      const multiplier = reverseDirection ? 1 : -1;
      
      if (currentSpeed === 0 && isHovered) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      // Calculate movement per frame (60fps)
      const movePerFrame = (currentSpeed / 60) * multiplier;
      positionRef.current += movePerFrame;

      // Get the width of one set of logos (we have 3 sets, so divide by 3)
      const contentSize = isHorizontal ? content.scrollWidth / 3 : content.scrollHeight / 3;

      // Reset position for seamless loop
      if (reverseDirection) {
        // Moving right
        if (positionRef.current >= contentSize) {
          positionRef.current = 0;
        }
      } else {
        // Moving left
        if (positionRef.current <= -contentSize) {
          positionRef.current = 0;
        }
      }

      // Apply transform
      if (isHorizontal) {
        content.style.transform = `translateX(${positionRef.current}px)`;
      } else {
        content.style.transform = `translateY(${positionRef.current}px)`;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [speed, hoverSpeed, isHovered, direction, reverseDirection, isHorizontal]);

  const renderLogo = (logo, key, index) => {
    if (typeof logo === 'string') {
      return (
        <div
          key={key}
          className={`logo-item ${scaleOnHover ? 'logo-item-hover' : ''}`}
          style={{
            height: `${logoHeight}px`,
            [isHorizontal ? 'marginRight' : 'marginBottom']: `${gap}px`,
            flexShrink: 0
          }}
        >
          <img
            src={logo}
            alt={`Logo ${index + 1}`}
            style={{ height: '100%', width: 'auto', objectFit: 'contain' }}
          />
        </div>
      );
    }
    
    return (
      <div
        key={key}
        className={`logo-item ${scaleOnHover ? 'logo-item-hover' : ''}`}
        style={{
          height: `${logoHeight}px`,
          [isHorizontal ? 'marginRight' : 'marginBottom']: `${gap}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {logo}
      </div>
    );
  };

  const containerClasses = `logo-loop-container ${isHorizontal ? 'horizontal' : 'vertical'} ${className}`;
  const fadeClasses = fadeOut ? 'fade-out' : '';

  return (
    <div
      ref={containerRef}
      className={`${containerClasses} ${fadeClasses}`}
      style={{
        width: isHorizontal ? width : 'auto',
        height: isHorizontal ? 'auto' : width,
        '--fade-color': fadeOutColor,
        ...style
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={ariaLabel}
    >
      <div
        ref={contentRef}
        className={`logo-loop-content ${isHorizontal ? 'horizontal' : 'vertical'}`}
      >
        {/* First set of logos */}
        {logos.map((logo, index) => renderLogo(logo, `set1-${index}`, index))}
        {/* Duplicate sets for seamless loop - we need at least 2 duplicates */}
        {logos.map((logo, index) => renderLogo(logo, `set2-${index}`, index))}
        {logos.map((logo, index) => renderLogo(logo, `set3-${index}`, index))}
      </div>
    </div>
  );
};

export default LogoLoop;
